import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, Loader2 } from 'lucide-react';
import { trainerKalenderAPI } from '@/lib/api';

/* Leads über den Trainer-Link (28.09.2026): Interessenten eintragen, z. B. aus
   dem Friseursalon. Nur mit Einverständnis der Person; der Link sieht danach
   nur Vorname und groben Stand, keine Nummern. */
const P1_ROT = '#E60000';
const LEER = { vorname: '', nachname: '', telefon: '', fuer_wen: 'erwachsen', kind_alter: '', interesse: '', notiz: '', einverstanden: false, whatsapp_ok: false };
const INTERESSEN = ['Kickboxen / Muay Thai', 'BJJ / Grappling', 'Frauentraining', 'Kinder-Kampfsport', 'Fitness / Gym', 'Weiß noch nicht'];
const datum = (iso) => iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.` : '';

export default function TippFormular({ token }) {
  const [f, setF] = useState(LEER);
  const [busy, setBusy] = useState(false);
  const [daten, setDaten] = useState(null);
  const lade = useCallback(async () => {
    try { setDaten((await trainerKalenderAPI.leads(token)).data); } catch { setDaten(null); }
  }, [token]);
  useEffect(() => { lade(); }, [lade]);
  const s = (k, v) => setF(x => ({ ...x, [k]: v }));

  const senden = async (e) => {
    e.preventDefault();
    if (!f.einverstanden) { toast.error('Bitte nur eintragen, wenn die Person einverstanden ist'); return; }
    setBusy(true);
    try {
      const r = await trainerKalenderAPI.leadAnlegen(token, { ...f, kind_alter: f.kind_alter ? Number(f.kind_alter) : null });
      toast.success(r.data.meldung); setF(LEER); lade();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };

  const feld = 'w-full h-11 rounded-lg border border-border bg-background px-3 text-base';
  return (
    <div className="max-w-xl mx-auto px-3 py-4 space-y-5" data-testid="link-leads">
      <form onSubmit={senden} className="rounded-xl border border-border bg-card p-4 space-y-3">
        <p className="text-base font-bold flex items-center gap-2"><UserPlus className="w-5 h-5" style={{ color: P1_ROT }} /> Interessent eintragen</p>
        <div className="grid grid-cols-2 gap-2">
          <input className={feld} placeholder="Vorname *" value={f.vorname} onChange={e => s('vorname', e.target.value)} required minLength={2} autoComplete="off" />
          <input className={feld} placeholder="Nachname" value={f.nachname} onChange={e => s('nachname', e.target.value)} autoComplete="off" />
        </div>
        <input className={feld} type="tel" inputMode="tel" placeholder="Handynummer *" value={f.telefon} onChange={e => s('telefon', e.target.value)} required autoComplete="off" />
        <div className="flex rounded-lg border border-border overflow-hidden text-sm font-semibold">
          {[['erwachsen', 'Für sich selbst'], ['kind', 'Für ein Kind']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => s('fuer_wen', k)} className={`flex-1 h-10 ${f.fuer_wen === k ? 'text-white' : ''}`} style={f.fuer_wen === k ? { background: P1_ROT } : undefined}>{l}</button>
          ))}
        </div>
        {f.fuer_wen === 'kind' && <input className={feld} inputMode="numeric" placeholder="Alter des Kindes" value={f.kind_alter} onChange={e => s('kind_alter', e.target.value.replace(/\D/g, '').slice(0, 2))} />}
        <select className={feld} value={f.interesse} onChange={e => s('interesse', e.target.value)}>
          <option value="">Interesse (optional)</option>
          {INTERESSEN.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <input className={feld} placeholder="Notiz, z. B. beste Uhrzeit (optional)" value={f.notiz} onChange={e => s('notiz', e.target.value)} maxLength={300} />
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1 w-5 h-5 accent-marke" checked={f.einverstanden} onChange={e => s('einverstanden', e.target.checked)} />
          <span>Die Person ist einverstanden, dass P1 sie anruft, um ein kostenloses Probetraining zu vereinbaren. *</span></label>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1 w-5 h-5 accent-marke" checked={f.whatsapp_ok} onChange={e => s('whatsapp_ok', e.target.checked)} />
          <span>Kontakt auch per WhatsApp ist okay.</span></label>
        <button type="submit" disabled={busy} className="w-full h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2" style={{ background: P1_ROT }} data-testid="tipp-senden">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />} Tipp abschicken
        </button>
        <p className="text-[11px] text-muted-foreground">Wir melden uns innerhalb von 24 Stunden. Die Daten gehen direkt an das P1 Performance Center und werden nur für die Kontaktaufnahme genutzt.</p>
      </form>

      {daten && (
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-sm font-bold">Deine Tipps <span className="font-normal text-muted-foreground">· {daten.gesamt} insgesamt, {daten.letzte_30_tage} in den letzten 30 Tagen</span></p>
          {daten.tipps.length === 0 ? <p className="text-sm text-muted-foreground mt-2">Noch keine – der erste zählt!</p> : (
            <ul className="mt-2 divide-y divide-border">
              {daten.tipps.map((t, i) => (
                <li key={i} className="py-2 flex items-center justify-between text-sm">
                  <span>{t.vorname} <span className="text-muted-foreground text-xs">{datum(t.erstellt_am)}</span></span>
                  <span className={`text-xs rounded-full px-2 py-0.5 ${t.stand === 'Mitglied geworden' ? 'bg-emerald-600 text-white' : 'bg-muted'}`}>{t.stand}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
